/**
 * CmBlock — the owning care manager's row in the drawer's "At a glance"
 * card: photo (or the initials fallback), name, and a "Copy intro email"
 * action. Extracted from WorkItemDrawer (UX-11 PR-2); restyled with the
 * drawer redesign.
 *
 * NO BIO (founder, 2026-07-26). The row used to print the CM's full
 * biography, which runs to three paragraphs on a real profile and swamped
 * a card whose whole job is the glance. A biography is not summary
 * information. The bio still reaches the family as a PARAGRAPH OF the
 * intro email this row copies, and the People tab remains where it is
 * read and edited — nothing was lost, it moved to where it belongs.
 *
 * The action (founder, same review) is labelled "Copy intro email", and
 * since 2026-07-26 that is the truth: it writes the whole assembled
 * Outlook-ready email (src/lib/introEmail.ts via introEmailCopy.ts), the
 * same one the People tab's button produces. It previously wrote the bare
 * bio text under a "Copy bio" label, so the office pasted a naked
 * biography where an entire email was wanted.
 *
 * Copy affordance sits on TextAction per the UX-11 gate ruling (Amanda
 * 2026-06-29 established TextAction as the drawer's quiet text affordance).
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CmPhoto } from '@/components/workspace/CmPhoto';
import { TextAction } from '@/components/workspace/TextAction';
import { useTenant } from '@/lib/auth';
import { canViewWorkspace } from '@/lib/capabilities';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { cmPhotoUrl } from '@/lib/staffAdmin';
import { readIntroEmailParagraphs } from '@/lib/introEmail';
import { copyIntroEmail, fetchCmPhotoDataUri } from '@/lib/introEmailCopy';
import {
  STATED_AVAILABILITY_CLASSES,
  STATED_AVAILABILITY_LABEL,
  currentWeekMonday,
  formatHours,
  type StatedAvailability,
} from '@/lib/capacity';
import { useCmCapacity } from '@/lib/capacityApi';

export interface CmBlockProps {
  tenantSlug: string;
  rosterId: string;
  rosterName: string | null | undefined;
  /** Intro-email inputs, resolved on the work-item detail read. The bio is
   *  NOT displayed here — it is a paragraph of the copied email. A null
   *  fact renders as a highlighted placeholder the unit lead fills in
   *  Outlook, exactly as on the People tab. */
  rosterBio: string | null | undefined;
  rosterCredentials?: string | null;
  rosterPreferredName?: string | null;
  rosterEmail?: string | null;
  rosterPhone?: string | null;
}

export function CmBlock({
  tenantSlug,
  rosterId,
  rosterName,
  rosterBio,
  rosterCredentials,
  rosterPreferredName,
  rosterEmail,
  rosterPhone,
}: CmBlockProps): ReactNode {
  const { whoami } = useTenant();
  // This week's new-client stance and last week's hours, read from the
  // capacity board's endpoint (the same facts the Availability tab shows)
  // by a seat that holds its view_workspace gate; no other seat asks.
  // Absent data renders nothing, and the pill shows only when the server
  // sent a stance.
  const capacity = useCmCapacity(tenantSlug, currentWeekMonday(), {
    enabled: tenantSlug.length > 0 && canViewWorkspace(whoami),
  });
  const capacityRow = capacity.careManagers.find((cm) => cm.roster_id === rosterId) ?? null;
  // Tenant paragraph templates (D2 config seam) with the platform fallback;
  // the canonical ['tenant-config', slug] cache entry, so the drawer shares
  // whatever the rest of the session already fetched.
  const tenantConfig = useTenantConfig(tenantSlug, { enabled: tenantSlug.length > 0 });
  const paragraphs = readIntroEmailParagraphs(tenantConfig.data?.config);

  // Copy flashes a short confirmation; per-render local concern.
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const handle = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(handle);
  }, [copied]);

  const onCopy = async (): Promise<void> => {
    // Resolve the photo first so Outlook embeds it; null (no photo, or any
    // failure) simply omits the photo paragraph rather than blocking.
    const photoDataUri = await fetchCmPhotoDataUri(cmPhotoUrl(tenantSlug, rosterId));
    try {
      await copyIntroEmail(
        {
          full_name: rosterName ?? '',
          credentials: rosterCredentials ?? null,
          preferred_name: rosterPreferredName ?? null,
          bio: rosterBio ?? null,
          email: rosterEmail ?? null,
          phone: rosterPhone ?? null,
          photo_data_uri: photoDataUri,
          sender_name: whoami?.display_name ?? null,
        },
        paragraphs,
      );
      setCopied(true);
    } catch {
      // A denied/unavailable clipboard leaves the label alone rather than
      // claiming a copy that never happened.
    }
  };

  return (
    <div className="flex items-center gap-2.5" data-testid="drawer-cm">
      <CmPhoto tenantSlug={tenantSlug} rosterId={rosterId} name={rosterName} size="md" />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-sage-800" data-testid="drawer-cm-name">
          {rosterName}
        </p>
        {capacityRow && (
          <span className="flex flex-wrap items-center gap-2">
            {capacityRow.new_client_stance !== undefined && <AvailabilityChip stance={capacityRow.new_client_stance} />}
            <span className="text-xs text-ink-soft" data-testid="drawer-cm-last-week">
              {`Last week ${formatHours(capacityRow.hours_worked_last_week) || '0'} h`}
            </span>
            {/* The board itself: the Team page's capacity tab. */}
            {canViewWorkspace(whoami) && (
              <Link
                to={`/t/${encodeURIComponent(tenantSlug)}/team?tab=capacity`}
                data-testid="drawer-cm-see-capacity"
                className="text-xs text-sage-700 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
              >
                See capacity
              </Link>
            )}
          </span>
        )}
        {/* -ml-2 cancels the pill's own px-2 so the action's TEXT sits on the
            name's left edge; the rest state is transparent, so without it the
            row reads as indented. */}
        <TextAction
          data-testid="drawer-cm-copy-intro-email"
          onClick={() => void onCopy()}
          className="mt-0.5 -ml-2"
        >
          {copied ? 'Copied ✓' : 'Copy intro email'}
        </TextAction>
      </div>
    </div>
  );
}

/** The care manager's new-client stance for this week, as the server sent
 *  it; never computed from hours. */
function AvailabilityChip({ stance: state }: { stance: StatedAvailability }): ReactNode {
  return (
    <span
      data-testid="drawer-cm-availability"
      data-availability={state}
      className={`inline-flex mt-0.5 rounded-pill px-2 py-0.5 text-xs font-semibold ${STATED_AVAILABILITY_CLASSES[state]}`}
    >
      {STATED_AVAILABILITY_LABEL[state]}
    </span>
  );
}
